import { Component, type ErrorInfo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ApiError } from '../lib/api-client';

/**
 * Application providers.
 *
 * The query client's defaults are the storefront's network policy, set once
 * here rather than per hook - retry behaviour that varies by call site is how
 * a checkout ends up retrying a request it should never repeat.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A catalogue is not real-time. A minute of freshness makes navigation
      // feel instant without ever showing a genuinely stale price at checkout,
      // where amounts are recomputed server-side anyway.
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,

      retry: (failureCount, error) => {
        // Never retry a client error: a 404 will still be a 404, and a 422 will
        // still be invalid. Retrying them only delays the error state.
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Mutations are never retried automatically. A retried order creation is
      // a duplicate order; the customer decides whether to try again.
      retry: false,
    },
  },
});

/**
 * Top-level error boundary.
 *
 * The last line of defence: a render error anywhere below shows a branded
 * recovery screen rather than a blank page. React does not catch errors in
 * event handlers or async code, so this covers render and lifecycle only -
 * everything else is handled by the query layer's error states.
 */
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (__BUILD_ENV__ === 'development') console.error('Unhandled render error', error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="container section">
        <div className="state" role="alert">
          <p className="label">Error</p>
          <h1 className="state__title">Something broke</h1>
          <p className="state__body">
            An unexpected error stopped this page rendering. Reloading usually clears it.
          </p>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AppErrorBoundary>
  );
}
